/**
 * EIS console palette — the `berry` chrome variant, restyled to the **EIS Design System 2025**
 * (palette A, design v2; decision T-31, amending T-8). The variant id stays `berry`; Berry's own
 * hues are retired.
 *
 * Source of truth: the Claude Design export "Group Contracts" v2, `_ds/…/colors_and_type.css`
 * (the Figma "Design System 2025 _ EIS" tokens). Nav navy `#3F4D67` / `#39465E`, action cyan
 * `#04A9F5`, app background `#F4F7FA`, white cards, text `#111111` / `#495057`, borders
 * `#CED4DA` / `#DEE2E6` / `#EEF1F4`. No component and no file of the export enters this repo.
 *
 * **AA wins over the prototype.** Where the design paints text below 4.5:1 the shipped value is
 * substituted; every pair is asserted in `__tests__/contrast.test.ts`:
 *
 *   | Use                                  | Prototype          | Ships                      |
 *   |--------------------------------------|--------------------|----------------------------|
 *   | contained button / selected tab      | `#04A9F5` (2.63)   | `primary.main #0277B5` (4.87) |
 *   | link                                 | `#007BFF` (3.98)   | `#0062CC` (5.80)           |
 *   | nav active text on navy              | `#04A9F5` (3.24)   | `#6FD2FF` (5.00)           |
 *   | nav default text on navy             | —                  | `#C8D1E0` (5.53)           |
 *   | helper / meta text on `#F4F7FA`      | `#6C757D` (4.36)   | `#636C74` (4.97)           |
 *
 * `#04A9F5` stays as `primary.light`, used only where no text sits on it (focus ring, hover tint).
 * The status chips are v2's tonal pairs and pass as designed (5.22–6.90:1); only `danger` is new.
 */

/**
 * The action ramp — cyan, through the design's `#04A9F5` (500, `primary.light`) and the AA-safe
 * `#0277B5` (700, `primary.main`). The pale steps are the design's hover/selection washes.
 */
export const brand = {
  50: '#F4FAFF',
  100: '#E3F4FD',
  200: '#CDEEFD',
  300: '#6FD2FF',
  400: '#1DC4E9',
  500: '#04A9F5',
  600: '#0391D3',
  700: '#0277B5',
  800: '#025F91',
  900: '#01476B',
} as const;

/** Link colour — the `_ds` link-hover `#0062CC`, since the design's `#007BFF` is 3.98:1. */
export const link = { light: '#0062CC', dark: '#6FD2FF' } as const;

/**
 * The EIS grey scale, plus the dark scheme's navies.
 *
 * 0–900 are the design's surfaces, rules and text: page `#F4F7FA`, hairline `#EEF1F4`, divider
 * `#DEE2E6`, border `#CED4DA`, disabled `#ABAEB2`, meta `#636C74` (the AA substitute for
 * `#6C757D`), secondary text `#495057`, primary text `#111111`. `dark*` are the dark scheme's four
 * surface levels, kept from before T-31 — decision 5: dark mode keeps its navy surfaces.
 */
export const neutral = {
  0: '#FFFFFF',
  50: '#F4F7FA',
  100: '#EEF1F4',
  200: '#DEE2E6',
  300: '#CED4DA',
  400: '#ABAEB2',
  500: '#636C74',
  600: '#495057',
  700: '#343A40',
  800: '#212529',
  900: '#111111',
  /** Zebra rows in a table — a step lighter than the hairline. */
  zebra: '#FAFBFC',
  /** Cards, drawer, app bar in the dark scheme. */
  darkPaper: '#111936',
  /** The page behind the cards, dark. */
  darkBackground: '#1A223F',
  /** Raised panels inside a card, dark. */
  darkLevel2: '#212946',
  /** The highest dark surface; also the dark divider. */
  darkLevel1: '#29314F',
} as const;

/**
 * The navigation rail. Light: the design's two-tone navy (`#3F4D67` panel, `#39465E` for the
 * expanded group's sub-items). Text on it is substituted for AA (header table): `#C8D1E0` at rest,
 * `#6FD2FF` for the active item; group headings are white. Dark: the rail takes the dark paper.
 */
export const navChrome = {
  light: {
    panel: '#3F4D67',
    sub: '#39465E',
    text: '#C8D1E0',
    heading: '#FFFFFF',
    active: '#6FD2FF',
  },
  dark: {
    panel: '#111936',
    sub: '#1A223F',
    text: '#C8D1E0',
    heading: '#FFFFFF',
    active: '#6FD2FF',
  },
} as const;

/**
 * Status tokens (backbone: status is a first-class visual concept).
 *
 * Every entity in this domain carries a lifecycle status and it appears in grids, headers and
 * chips. Defining the colours once here — instead of per screen — is what keeps a terminated
 * group policy and a terminated child-policy looking the same everywhere.
 *
 * Design v2's tonal chip: `main` is the text colour, `surface` the fill, `border` equals the
 * surface (the v2 chip has no visible border). `contrastText` is white on `main` for any filled
 * use. `suspended` and `terminated` share the neutral tone, as v2's "Archiviert"/"Verworfen" do —
 * the label tells them apart. `danger` is new with T-31 (FAILED).
 */
export type StatusToken =
  | 'draft'
  | 'active'
  | 'suspended'
  | 'terminated'
  | 'pending'
  | 'published'
  | 'danger';

export interface StatusColor {
  readonly main: string;
  readonly contrastText: string;
  readonly surface: string;
  readonly border: string;
}

export const statusLight: Readonly<Record<StatusToken, StatusColor>> = {
  /** v2 warning — amber. */
  draft: { main: '#8A5A00', contrastText: '#FFFFFF', surface: '#FFF1D6', border: '#FFF1D6' },
  /** v2 success — teal. */
  active: { main: '#0F6E69', contrastText: '#FFFFFF', surface: '#D9F3F1', border: '#D9F3F1' },
  /** v2 neutral. */
  suspended: { main: '#495057', contrastText: '#FFFFFF', surface: '#E9ECEF', border: '#E9ECEF' },
  /** v2 neutral. */
  terminated: { main: '#495057', contrastText: '#FFFFFF', surface: '#E9ECEF', border: '#E9ECEF' },
  /** v2 pending — lavender. */
  pending: { main: '#5B4BA8', contrastText: '#FFFFFF', surface: '#ECE9F8', border: '#ECE9F8' },
  /** v2 info — blue. */
  published: { main: '#0B5E82', contrastText: '#FFFFFF', surface: '#E3F4FD', border: '#E3F4FD' },
  /** v2 danger — red. */
  danger: { main: '#9B2B23', contrastText: '#FFFFFF', surface: '#FCE4E2', border: '#FCE4E2' },
};

/**
 * The dark equivalents: a dark surface with light text, each tone taken from the pre-T-31 dark
 * table's matching hue (teal-green, blue, amber, lavender, neutral, red).
 */
export const statusDark: Readonly<Record<StatusToken, StatusColor>> = {
  draft: { main: '#FFE57F', contrastText: '#111936', surface: '#3A2F12', border: '#3A2F12' },
  active: { main: '#69F0AE', contrastText: '#111936', surface: '#14372A', border: '#14372A' },
  suspended: { main: '#C8D1E0', contrastText: '#111936', surface: '#2A3350', border: '#2A3350' },
  terminated: { main: '#C8D1E0', contrastText: '#111936', surface: '#2A3350', border: '#2A3350' },
  pending: { main: '#B39DDB', contrastText: '#111936', surface: '#241D43', border: '#241D43' },
  published: { main: '#90CAF9', contrastText: '#111936', surface: '#14284A', border: '#14284A' },
  danger: { main: '#EF9A9A', contrastText: '#111936', surface: '#3A1F22', border: '#3A1F22' },
};

/**
 * Semantic colours, from the v2 families and darkened until white reads on them (a contained
 * error button, a filled success chip). Warning is the draft amber, info the published blue.
 * `warningSurface` / `warningText` are the soft alert pair the design specifies. Dark mode is
 * the light variants that read on navy.
 */
export const semantic = {
  light: {
    success: '#1E7362',
    warning: '#8A5A00',
    error: '#C62828',
    info: '#0B5E82',
  },
  dark: {
    success: '#69F0AE',
    warning: '#FFE57F',
    error: '#EF9A9A',
    info: '#90CAF9',
  },
} as const;

/** The design's soft warning alert — `#856404` on `#FFF3CD`. */
export const warningAlert = { surface: '#FFF3CD', text: '#856404' } as const;

/**
 * A selected toggle (the DE/EN switch, the version switcher): link blue on the palest cyan.
 *
 * It gets its own explicit pair rather than `action.selected` + `primary.main`, because
 * `action.selected` is a translucent overlay: what the text actually sits on depends on the
 * surface underneath. Naming both halves makes the pair assertable.
 */
export const selection = {
  light: { surface: '#F4FAFF', text: '#0062CC' },
  dark: { surface: '#1B2A4E', text: '#90CAF9' },
} as const;

/** Text colours for the dark scheme — kept from before T-31 (decision 5). */
export const darkText = {
  primary: '#BDC8F0',
  secondary: '#97A3D0',
  disabled: '#6B77A8',
} as const;
